import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  INHABITANT_SHORT_LIST_API,
  INHABITANT_SINGLE_FIELD_API,
} from '../../../constants/api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { INHABITANT_STATUS } from '../../../constants/inhabitant'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useModals,
  useQueryParamSubTab,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getVisibleFilters } from '../../../utils/filter'
import { getSubSearchParam, isSubArchive } from '../../../utils/route'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import {
  getInhabitantTableItemActions,
  getInhabitantTableMassActions,
  INHABITANT_TABLE_MODALS_CONFIG,
  InhabitantTableLayout,
  useInhabitantTableActions,
  useInhabitantTablePage,
} from '../../Inhabitant'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'
import { getEntityTabFilterStateApi } from '../EntityTab.api'
import { getEntityTabFilterBarApiUrl } from '../EntityTab.utils'
import { EntityTabSubTabs, getEntityTabSubTabItems } from '../EntityTabSubTabs'

import {
  ENTITY_TAB_INHABITANTS_SUB_TAB_ORDER,
  ENTITY_TAB_INHABITANTS_SUB_TAB_TEXT_KEY,
  QA_NAME_ENTITY_TAB_INHABITANTS_ADD_NEW,
} from './EntityTabInhabitants.constants'
import {
  getEntityTabInhabitantsExcludeFilters,
  getEntityTabInhabitantsSubTabQaName,
} from './EntityTabInhabitants.utils'

import * as styles from './EntityTabInhabitants.module.css'

import PlusIcon from '../../../icons/plus.svg'
import UserIcon from '../../../icons/user.svg'

function EntityTabInhabitants({
  canViewArchive,
  counters = EMPTY_OBJECT,
  entity,
  qaName = QA_NAME_ENTITY_TAB_INHABITANTS_ADD_NEW,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  templateEntityApiParams,
  onApplyTableConfig,
  onCreate,
  onEntityReload,
  onRegisterReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(INHABITANT_TABLE_MODALS_CONFIG)

  const subTabOrder = canViewArchive
    ? ENTITY_TAB_INHABITANTS_SUB_TAB_ORDER
    : [SUBMENU_DEFAULT_VALUE]

  const { activeSubTab, setActiveSubTab } = useQueryParamSubTab({
    allowedValues: subTabOrder,
    paramKey: SUBMENU_PARAM_KEY,
    readValue: getSubSearchParam,
  })

  const isArchiveSubTab = isSubArchive(activeSubTab)

  // The inhabitants list endpoints expect the scope as a scalar id, not a list.
  const scopeFilters = scopeFilterKey ? { [scopeFilterKey]: scopeId } : null

  const tablePage = useInhabitantTablePage({
    asyncTableOptions: {
      extraFilters: scopeFilters,
      filterStateApi: getEntityTabFilterStateApi({
        entityPlural: entity.plural,
        objectId: scopeId,
        sub: activeSubTab,
      }),
      listApi: INHABITANT_SHORT_LIST_API,
      withoutSubMenu: true,
    },
    entity,
    extraSelectionResetDeps: [activeSubTab, scopeId],
    isBrowserTabTitleEnabled: false,
    sub: activeSubTab,
    onApplyTableConfig,
  })

  const {
    availableFilters,
    excludeIds,
    filterValues,
    handleChangeShouldReloadFilters,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    handleUnselectAllPages,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selectedIds,
    shouldReloadFilters,
  } = tablePage

  async function handleReloadAfterMassAction() {
    onEntityReload?.()

    return handleReloadTableAfterMassAction()
  }

  const {
    handleChangeAddress,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  } = useInhabitantTableActions({
    excludeIds,
    filterValues: { ...filterValues, ...scopeFilters },
    pagination,
    selectAllPages,
    selectedIds,
    sub: activeSubTab,
    templateEntityApiParams,
    unselectAllPages: handleUnselectAllPages,
    onCleanSelection: handleCleanSelection,
    onCloseEmailFormModal: closeEmailFormModal,
    onCloseModal: closeModal,
    onReload: handleReloadAfterMassAction,
    onSetEmailFormModal: setEmailFormModal,
    onSetModal: setModal,
  })

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [activeSubTab, scopeId])

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  function handleAddInhabitant() {
    onCreate?.({ onReloadList: handleReload })
  }

  const massActions = getInhabitantTableMassActions({
    permissions,
    sub: activeSubTab,
    t,
  })

  const subTabItems = getEntityTabSubTabItems({
    counters,
    subTabOrder,
    t,
    textKeys: ENTITY_TAB_INHABITANTS_SUB_TAB_TEXT_KEY,
  })

  const hasInhabitants = items?.items?.length > 0

  const hasTable = hasInhabitants || isLoading

  const shouldShowCreateActions =
    shouldShowCreateAction &&
    !isArchiveSubTab &&
    (permissions?.can_create || isLoading)

  const hasVisibleFilters = getVisibleFilters(availableFilters).length > 0

  const shouldCollapseToolbar =
    !isLoading &&
    ((!hasInhabitants && !hasFilterChanged) ||
      (!hasVisibleFilters && !shouldShowCreateActions))

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName,
    isLoading,
    qaName,
    shouldShowActions: shouldShowCreateActions && !hasFilterChanged,
    text: t('InhabitantTable.AddNewBtnKey'),
    onClick: handleAddInhabitant,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div className={styles.EntityTabInhabitants} ref={pageScrollWrapperRef}>
        {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
        {subTabOrder.length > 1 ? (
          <EntityTabSubTabs
            activeValue={activeSubTab}
            className={styles.subTabs}
            getItemQaName={getEntityTabInhabitantsSubTabQaName}
            items={subTabItems}
            onChange={setActiveSubTab}
          />
        ) : null}
        {shouldCollapseToolbar ? null : (
          <div className={styles.toolbar}>
            <FilterBar
              responsive
              apiUrl={getEntityTabFilterBarApiUrl({
                baseApi: INHABITANT_SINGLE_FIELD_API,
                scopeFilterKey,
                scopeId,
              })}
              availableFilters={availableFilters}
              className={styles.filterBar}
              entity={entity}
              excludeFilters={getEntityTabInhabitantsExcludeFilters(
                scopeFilterKey
              )}
              filterTriggerVariant={
                FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
              }
              filterValues={filterValues}
              isSkeleton={isLoading}
              objectId={scopeId}
              ordering={ordering}
              pagination={{ per_page: pagination?.pageSize }}
              setFilterValues={handleSetFilterValues}
              shouldReloadValues={shouldReloadFilters}
              sub={activeSubTab}
              onChangeShouldReloadValues={handleChangeShouldReloadFilters}
              onReload={handleReload}
            />
            <PageActions className={styles.actions} isLoading={isLoading}>
              {shouldShowCreateActions ? (
                <Button.Text
                  beforeIconProps={{ Icon: PlusIcon }}
                  isSkeleton={isLoading}
                  qaName={qaName}
                  text={t('InhabitantTable.AddNewBtnKey')}
                  onClick={handleAddInhabitant}
                />
              ) : null}
            </PageActions>
          </div>
        )}
        <InhabitantTableLayout
          emptyState={
            <PageEmptyState
              EntityIcon={UserIcon}
              actions={emptyStateActions}
              className={styles.emptyPage}
              text={t('InhabitantTable.NoDataKey')}
            />
          }
          extraModals={modals.map(([id, element]) => (
            <Fragment key={id}>{element}</Fragment>
          ))}
          getInhabitantItemActions={getInhabitantTableItemActions}
          hasTable={hasTable}
          massActions={massActions}
          rowStatuses={INHABITANT_STATUS}
          setItemAction={setItemAction}
          setMassAction={setMassAction}
          tablePage={tablePage}
          onChangeAddress={handleChangeAddress}
        />
        {hasTable ? (
          <TablePagination
            responsive
            className={styles.pagination}
            isSkeleton={isLoading}
            value={pagination}
            onChange={handleSetPagination}
          />
        ) : null}
      </div>
    </ScreenContainerRef>
  )
}

// This module is reachable from the Email barrel (via RequestProfile tabs), so it
// must not read Email exports at module scope — consumers apply withDraftProviders
// with EmailDraftTableProvider themselves to avoid a circular import crash.
export default EntityTabInhabitants
